import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import linkedInService from 'features/social-media/services/linkedinApi';
import twitterService from 'features/social-media/services/twitterApi';
import { useSocialMediaContext, PlatformConnection } from 'features/social-media/SocialMediaContext';
import { LoaderIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import SocialMediaDialog from './Dialog';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface FormValues {
  postName: string;
  date: string;
  time: string;
}

interface SavePublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function SavePublishDialog({ open, onOpenChange }: SavePublishDialogProps) {
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [schedulePost, setSchedulePost] = useState(false);
  
  const { 
    socialMediaContent,
  } = useSocialMediaContext();

  const { user } = useSelector((state: RootState) => state.auth);
  const [searchParams] = useSearchParams();
  const currentPlatform = searchParams.get('name')?.toLowerCase() as 'linkedin' | 'x';

  // Add debug logs
  console.log("User state:", user);
  console.log("Social connections:", user?.socialConnections);
  console.log("Current platform:", currentPlatform);

  // Check connection status from global auth state for both platforms
  const isConnected = user?.socialConnections?.[currentPlatform]?.isConnected || false;
  console.log("Is platform connected:", isConnected);

  // Get usernames from global state
  const linkedinUsername = user?.socialConnections?.linkedin?.username;
  const twitterUsername = user?.socialConnections?.x?.username;
  console.log("Twitter username:", twitterUsername);
  console.log("LinkedIn username:", linkedinUsername);

  useEffect(() => {
    // When dialog opens, check if platform is connected
    console.log("Dialog open state changed:", open);
    console.log("Connection state when dialog opens:", isConnected);
    
    if (open) {
      if (!isConnected) {
        console.log("Platform not connected, showing connection dialog");
        onOpenChange(false); // Close the publish dialog
        setIsConnectionDialogOpen(true); // Show connection dialog
      } else {
        console.log("Platform is connected, keeping publish dialog open");
      }
    }
  }, [open, isConnected, onOpenChange]);

  // Set up default time as current time in HH:mm format
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const form = useForm<FormValues>({
    defaultValues: {
      postName: '',
      date: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD
      time: getCurrentTime(), // Format: HH:mm
    },
  });

  const saveAndPublishTwitter = useMutation({
    mutationKey: ['save-and-publish-twitter'],
    mutationFn: twitterService.postTweet,
    onSuccess: () => {
      toast.success('X Post');
      onOpenChange(false);
    },
    onError: () => {
      toast.error('Only Premium X users can publish posts');
    }
  });

  const saveAndPublishLinkedIn = useMutation({
    mutationKey: ['save-and-publish-linkedin'],
    mutationFn: linkedInService.postLinkedInPost,
    onSuccess: () => {
      toast.success('Posted on LinkedIn');
      onOpenChange(false);
    },
  });

  const convertToPlainText = useMutation({
    mutationFn: linkedInService.convertToPlainText,
    mutationKey: ['convert-to-plain-text'],
  });

  const saveAndScheduleLinkedIn = useMutation({
    mutationKey: ['save-and-schedule-linkedin'],
    mutationFn: linkedInService.scheduleLinkedInPost,
    onSuccess: () => {
      toast.success('Post schedule on LinkedIn');
      onOpenChange(false);
    },
  });

  const saveAndSchedule = useMutation({
    mutationKey: ['save-and-schedule-twitter'],
    mutationFn: twitterService.scheduleTweet,
    onSuccess: () => {
      toast.success('Post schedule on X');
      onOpenChange(false);
    },
  });

  const linkedInAuth = useMutation({
    mutationKey: ['initiate-linkedin-auth'],
    mutationFn: linkedInService.getAuthUrl,
    onSuccess: ({ data }) => {
      // Open LinkedIn auth URL in a new window
      window.open(data.url, '_blank');
    },
    onError: () => {
      toast.error('Failed to initiate LinkedIn authentication');
    }
  });

  const twitterAuth = useMutation({
    mutationKey: ['initiate-twitter-auth'],
    mutationFn: twitterService.getAuthUrl,
    onSuccess: ({ data }) => {
      // Open Twitter auth URL in a new window
      window.open(data.url, '_blank');
    },
    onError: () => {
      toast.error('Failed to initiate Twitter authentication');
    }
  });

  const handleSubmit = (data: FormValues) => {
    const postId = socialMediaContent?.[0]?.postId;
    console.log(currentPlatform, postId, linkedinUsername, twitterUsername);

    if (currentPlatform === 'linkedin') {
      if (!postId || !linkedinUsername) {
        toast.error('Missing required information');
        return;
      }

      if (!schedulePost) {
        // Direct publish - no need for plain text conversion
        saveAndPublishLinkedIn.mutate({
          target_handle: linkedinUsername,
          id: postId
        });
      } else {
        // Only convert to plain text for scheduled posts
        if (socialMediaContent) {
          convertToPlainText
            .mutateAsync({
              content: socialMediaContent[0].markdownContent,
            })
            .then((res) => {
              saveAndScheduleLinkedIn.mutate({
                target_handle: linkedinUsername,
                scheduled_at: new Date(`${data.date}T${data.time}:00`).toUTCString(),
                post: res.data.plain_text,
                img_urls: [socialMediaContent[0].image],
                erase_img_after_publish: false,
                id: postId
              });
            });
        }
      }
    } else {
      // X (Twitter) flow
      if (!postId || !twitterUsername) {
        toast.error('Missing required information');
        return;
      }

      if (!schedulePost) {
        saveAndPublishTwitter.mutate({
          target_handle: twitterUsername,
          id: postId
        });
      } else {
        if (socialMediaContent) {
          saveAndSchedule.mutate({
            target_handle: twitterUsername,
            scheduled_at: new Date(`${data.date}T${data.time}:00`).toUTCString(),
            id: postId
          });
        }
      }
    }
  };

  // Listen for auth success messages from popup windows
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log("Received message:", event.data);
      
      if (event.data.type === 'TWITTER_AUTH_SUCCESS' || event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
        console.log("Auth success message received");
        // Add a delay to ensure state is updated
        setTimeout(() => {
          setIsConnectionDialogOpen(false);
          onOpenChange(true); // Reopen the publish dialog
        }, 500); // Increased delay to ensure state updates
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onOpenChange]);

  return (
    <>
      {/* Connection Dialog */}
      <SocialMediaDialog
        isOpen={isConnectionDialogOpen}
        onClose={() => {
          console.log("Closing connection dialog");
          setIsConnectionDialogOpen(false);
        }}
        platform={currentPlatform}
        socialMediaContent={socialMediaContent}
      />

      {/* Save & Publish Dialog - Only shown when connected */}
      <Dialog 
        open={open && isConnected} 
        onOpenChange={(value) => {
          console.log("Publish dialog state changing to:", value);
          onOpenChange(value);
          if (!value) {
            setSchedulePost(false);
            form.reset();
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Save & {schedulePost ? 'Schedule' : 'Publish'} post?
            </DialogTitle>

            {/* <DialogDescription>
              When you publish or schedule posts, a copy of the original is saved
              in the default folder.
            </DialogDescription> */}
          </DialogHeader>

          <form
            className="flex flex-col gap-2"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div>
              <Label htmlFor="postName">Post name</Label>
              <Input {...form.register('postName')} />
            </div>

            {schedulePost && (
              <>
                <Separator className="my-2" />

                <div>
                  <Label htmlFor="date">Post Date</Label>
                  <Input {...form.register('date')} type="date" id="date" />
                </div>
                <div>
                  <Label htmlFor="time">Post Time</Label>
                  <Input
                    {...form.register('time')}
                    type="time"
                    id="time"
                    step="60" // Show only hours and minutes, no seconds
                  />
                </div>
              </>
            )}

            <DialogFooter className="mt-4">
              <Button
                variant="customSecondary"
                size="sm"
                type="button"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="customSecondary"
                size="sm"
                type="button"
                onClick={() => {
                  setSchedulePost((prev) => !prev);
                }}
              >
                {schedulePost ? 'Publish Now' : 'Schedule'}
              </Button>
              <Button
                variant="customPrimary"
                size="sm"
                type="submit"
                disabled={
                  saveAndPublishTwitter.isPending ||
                  saveAndPublishLinkedIn.isPending ||
                  saveAndSchedule.isPending ||
                  saveAndScheduleLinkedIn.isPending
                }
              >
                {saveAndPublishTwitter.isPending ||
                saveAndPublishLinkedIn.isPending ||
                saveAndSchedule.isPending ||
                saveAndScheduleLinkedIn.isPending ? (
                  <>
                    <LoaderIcon className="animate-spin" />
                    {schedulePost ? 'Scheduling' : 'Publishing'}
                  </>
                ) : (
                  (schedulePost ? 'Schedule' : 'Publish')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
